import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { SmartLinkDecisionAction } from 'src/app/core/dtos/enums/smart-link-decision-action';
import {
  REJECT_REASONS,
  SmartLinkRejectReason,
} from 'src/app/core/dtos/enums/smart-link-reject-reason';
import {
  entityTypeLabel,
  ISmartLink,
  rejectReasonLabel,
  SmartLinkEvidenceField,
} from 'src/app/core/models/i-smart-link';
import { ISmartLinkRunContent } from 'src/app/core/models/i-smart-link-run-request';
import {
  buildEvidenceRows,
  ISmartLinkEvidenceRow,
} from 'src/app/core/models/smart-link-snippet';

export interface ISmartLinkRejection {
  id: string;
  reason: SmartLinkRejectReason;
}

// One surface to light. The clauses travel with it rather than the smart link id, because the
// card is the only thing that knows which evidence the click stood for.
export interface ISmartLinkMentionFlashTarget {
  field: SmartLinkEvidenceField;
  clauses: string[];
}

// recordUrl and boxLabel are panel knowledge, so they arrive resolved rather than being looked
// up again per card.
export interface ISmartLinkCandidate {
  item: ISmartLink;
  recordUrl: string | null;
  boxLabel: string | null;
  // NOTE: PM-165: the record is already in its box (Private Markets Fund only — every other
  // type is filtered out of the queue before it becomes a candidate at all). Accepting still
  // does something, so the card stays actionable; this only changes how it reads.
  alreadyTagged: boolean;
}

@Component({
  selector: 'app-smart-link-card',
  templateUrl: './smart-link-card.component.html',
  styleUrls: ['./smart-link-card.component.scss'],
})
export class SmartLinkCardComponent {
  // Every reading of one mention, in queue order. More than one means the run could not choose
  // between them, and they share a card because they are one decision.
  @Input() candidates: ISmartLinkCandidate[] = [];
  @Input() isEditMode = true;
  // The panel's in-flight list, so a card holding several candidates needs no per-candidate input.
  @Input() decidingIds: string[] = [];
  // Only reached by a lone candidate: a card that holds the alternatives itself already shows them.
  @Input() alternativeNames: string[] = [];
  // The content the run was started from, for showing the mention in its sentence. Null on a run
  // adopted at page load, whose content this panel never saw.
  @Input() scanContent: ISmartLinkRunContent | null = null;
  // Off inside typed suggestion groups, where the group header already names the type.
  @Input() showTypeChip = true;

  @Output() handleAccept = new EventEmitter<string>();
  @Output() handleReject = new EventEmitter<ISmartLinkRejection>();
  @Output() handleRestore = new EventEmitter<string>();
  @Output() handleUnlink = new EventEmitter<string>();
  @Output() handleFlashMention = new EventEmitter<
    ISmartLinkMentionFlashTarget[]
  >();

  @ViewChild('cardRoot') private cardRoot: ElementRef<HTMLElement>;

  readonly rejectReasons = REJECT_REASONS;

  private evidenceRowsCache: ISmartLinkEvidenceRow[] = [];
  private evidenceRowsBuiltFrom: {
    item: ISmartLink | null;
    scanContent: ISmartLinkRunContent | null;
  } | null = null;

  // Candidates are readings of one mention, so any of them quotes it — the first will do.
  get leadItem(): ISmartLink | null {
    return this.candidates[0]?.item ?? null;
  }

  // Building a row scans the whole article body, so it is cached against the inputs it came from:
  // a plain template call would redo that for every card on every poll tick. Memoised rather than
  // done in ngOnChanges because inputs can also be assigned straight onto the instance.
  get evidenceRows(): ISmartLinkEvidenceRow[] {
    const built = this.evidenceRowsBuiltFrom;
    const item = this.leadItem;
    if (!built || built.item !== item || built.scanContent !== this.scanContent) {
      this.evidenceRowsBuiltFrom = { item, scanContent: this.scanContent };
      this.evidenceRowsCache = item
        ? buildEvidenceRows(item, this.scanContent)
        : [];
    }
    return this.evidenceRowsCache;
  }

  get isAmbiguous(): boolean {
    return this.candidates.length > 1;
  }

  // Ambiguity siblings share a display name to within a roman numeral, and the record id is the
  // only field on the contract that separates them.
  get showRecordId(): boolean {
    return this.isAmbiguous || this.alternativeNames.length > 0;
  }

  get showAlternativeHint(): boolean {
    return !this.isAmbiguous && this.alternativeNames.length > 0;
  }

  // Skipped rather than rendered empty: read-only inside a typed group leaves nothing for the row
  // to hold, and an empty flex row still takes the column's gap with it.
  showRecordMeta(candidate: ISmartLinkCandidate): boolean {
    return (
      this.showTypeChip ||
      this.isAmbiguous ||
      this.isEditMode ||
      this.isDismissed(candidate) ||
      this.isLinked(candidate) ||
      this.isAlreadyTagged(candidate)
    );
  }

  // The corner can only speak for one record: with a choice in the card each reading is scored
  // separately, so the meters move beside the records they score.
  get cornerCandidate(): ISmartLinkCandidate | null {
    return this.isAmbiguous ? null : (this.candidates[0] ?? null);
  }

  // The panel binds [candidates] to a mapping expression, so every change-detection pass arrives
  // with new candidate objects. Unkeyed, NgForOf rebuilds the rows, and a button replaced between
  // mousedown and mouseup never fires — which takes Reject out altogether, since MatMenuTrigger
  // listens for mousedown itself and so triggers the pass that destroys it.
  trackCandidate(_index: number, candidate: ISmartLinkCandidate): string {
    return candidate.item.smart_link_id;
  }

  typeLabel(candidate: ISmartLinkCandidate): string {
    return entityTypeLabel(candidate.item);
  }

  isDeciding(candidate: ISmartLinkCandidate): boolean {
    return this.decidingIds.includes(candidate.item.smart_link_id);
  }

  isDismissed(candidate: ISmartLinkCandidate): boolean {
    return candidate.item.decision_action === SmartLinkDecisionAction.REJECT;
  }

  isLinked(candidate: ISmartLinkCandidate): boolean {
    return candidate.item.decision_action === SmartLinkDecisionAction.ACCEPT;
  }

  // NOTE: undecided but already sitting in its box (see ISmartLinkCandidate.alreadyTagged) —
  // distinct from isLinked, which means this suggestion itself was accepted.
  isAlreadyTagged(candidate: ISmartLinkCandidate): boolean {
    return !this.isLinked(candidate) && candidate.alreadyTagged;
  }

  // No box means the accept was only ever recorded against the run, so there is nothing to
  // take back out.
  linkedLocationLabel(candidate: ISmartLinkCandidate): string {
    return candidate.boxLabel
      ? `In ${candidate.boxLabel}`
      : 'Recorded only — no box';
  }

  reasonLabel(candidate: ISmartLinkCandidate): string {
    return candidate.item.decision_reason
      ? rejectReasonLabel(candidate.item.decision_reason)
      : '';
  }

  // The panel asks which card a decided suggestion was in so it can hand focus to a neighbour.
  holds(smartLinkId: string): boolean {
    return this.candidates.some(
      (candidate) => candidate.item.smart_link_id === smartLinkId,
    );
  }

  // preventScroll because the queue is a capped scrollport: the neighbour of a just-decided card
  // is already on screen, and letting focus() scroll jumped the view instead of nudging it.
  focus(): void {
    this.cardRoot?.nativeElement.focus({ preventScroll: true });
  }

  // The handlers guard on isDeciding themselves because the buttons carry aria-disabled rather
  // than disabled: a native [disabled] flipping mid-decision blurs the focused button to <body>
  // and loses the user's place in the queue.
  onAccept(candidate: ISmartLinkCandidate): void {
    if (this.isDeciding(candidate)) {
      return;
    }
    this.handleAccept.emit(candidate.item.smart_link_id);
  }

  onRestore(candidate: ISmartLinkCandidate): void {
    if (this.isDeciding(candidate)) {
      return;
    }
    this.handleRestore.emit(candidate.item.smart_link_id);
  }

  onUnlink(candidate: ISmartLinkCandidate): void {
    if (this.isDeciding(candidate)) {
      return;
    }
    this.handleUnlink.emit(candidate.item.smart_link_id);
  }

  // NOTE: Not gated on isEditMode, unlike every handler above it — see ADR-0007.
  onFlashEvidence(row: ISmartLinkEvidenceRow): void {
    this.handleFlashMention.emit([{ field: row.field, clauses: row.clauses }]);
  }

  // NOTE: The card is the wider-scoped second target — a row lights the field it quotes, the
  // card lights every field cited. Why both triggers exist is ADR-0007.
  onCardClick(event: MouseEvent): void {
    if ((event.target as Element)?.closest('button, a')) {
      return;
    }
    const targets = this.evidenceRows.map((row) => ({
      field: row.field,
      clauses: row.clauses,
    }));
    if (targets.length === 0) {
      return;
    }
    this.handleFlashMention.emit(targets);
  }

  chooseReason(
    candidate: ISmartLinkCandidate,
    reason: SmartLinkRejectReason,
  ): void {
    if (this.isDeciding(candidate)) {
      return;
    }
    this.handleReject.emit({ id: candidate.item.smart_link_id, reason });
  }
}
